/**
 * WebhookHealthPage — per-topic webhook delivery health (#1438).
 *
 * Revives the webhook-health surface as a view of the `WebhooksTabs` strip. For
 * each provider (shopify / onfleet) it renders a per-topic table sourced from
 * `webhook_events`: Total / Completed / Failed / error-rate / last-seen /
 * last-error, plus a derived STATUS badge. Read-only — resolve / replay /
 * delete live on `/webhooks/failures` (`webhook_failures:write`).
 *
 * The badge is derived HERE from the raw metrics the backend returns (it ships
 * only numbers + `expected`, never a label — ADR: display logic stays in the
 * view). Derivation is by NUMBER, never by translated string:
 *
 *   total === 0 && expected      → Silent   (declared topic went quiet)
 *   !expected                    → Drift    (received an undeclared topic)
 *   errorRate >= 10              → Unhealthy
 *   failed > 0                   → Degraded (errorRate below the ceiling)
 *   else                         → Healthy
 *
 * `errorRate` is a percentage that may be null (0 completed+failed) — rendered
 * as an em dash, NEVER coerced to 0%. The window (6h/24h/3d/7d/30d) persists to
 * the `?windowHours=N` URL param so a view is shareable and reload-stable.
 *
 * i18n reuses the existing `webhookHealth.*` vocabulary: the badge labels,
 * table headers, window switcher, drift alert and totals are shared UI terms,
 * so both provider blocks read the same (shopify-namespaced) keys rather than
 * duplicating a parallel onfleet set.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  PageShell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Card,
  Button,
} from '@resource-kit/ui/react';
import { cn } from '@resource-kit/ui';
import { formatDateTime, formatRelativeFromNow } from '@lib/datetime';
import { WebhooksTabs } from '@features/webhooks';
import {
  DEFAULT_WEBHOOK_HEALTH_WINDOW,
  asWebhookHealthWindow,
  type WebhookHealthWindow,
  type WebhookProviderHealth,
  type WebhookTopicHealth,
} from '@/services/webhook-health';
import { useWebhookHealth } from '../hooks/useWebhookHealth';

/** Error-rate percentage at or above which a topic is flagged Unhealthy. */
const UNHEALTHY_ERROR_RATE_PCT = 10;

const EM_DASH = '—';

type HealthBadge = 'silent' | 'drift' | 'unhealthy' | 'degraded' | 'healthy';

/** Derive the display badge from raw metrics — by number, never by label. */
function deriveBadge(topic: WebhookTopicHealth): HealthBadge {
  if (topic.total === 0 && topic.expected) return 'silent';
  if (!topic.expected) return 'drift';
  if (topic.errorRate != null && topic.errorRate >= UNHEALTHY_ERROR_RATE_PCT) {
    return 'unhealthy';
  }
  if (topic.failed > 0) return 'degraded';
  return 'healthy';
}

const BADGE_VARIANT: Record<HealthBadge, 'success' | 'secondary' | 'destructive' | 'outline'> = {
  healthy: 'success',
  degraded: 'secondary',
  unhealthy: 'destructive',
  silent: 'outline',
  drift: 'destructive',
};

const WINDOW_OPTIONS: { hours: WebhookHealthWindow; labelKey: string }[] = [
  { hours: 6, labelKey: 'webhookHealth.shopify.windowSwitcher.6h' },
  { hours: 24, labelKey: 'webhookHealth.shopify.windowSwitcher.24h' },
  { hours: 72, labelKey: 'webhookHealth.shopify.windowSwitcher.3d' },
  { hours: 168, labelKey: 'webhookHealth.shopify.windowSwitcher.7d' },
  { hours: 720, labelKey: 'webhookHealth.shopify.windowSwitcher.30d' },
];

const PROVIDER_TITLE_KEY: Record<WebhookProviderHealth['provider'], string> = {
  shopify: 'webhookHealth.shopify.title',
  onfleet: 'webhookHealth.onfleet.deliveryTitle',
};

function formatErrorRate(rate: number | null): string {
  return rate == null ? EM_DASH : `${rate}%`;
}

function StatusBadge({ badge }: { badge: HealthBadge }) {
  const { t } = useTranslation();
  return (
    <Badge variant={BADGE_VARIANT[badge]} data-testid={`whh-badge-${badge}`}>
      {t(`webhookHealth.shopify.status.${badge}`)}
    </Badge>
  );
}

function LastSeenCell({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">{EM_DASH}</span>;
  return <span title={formatDateTime(value)}>{formatRelativeFromNow(value)}</span>;
}

function LastErrorCell({ topic }: { topic: WebhookTopicHealth }) {
  const { t } = useTranslation();
  const err = topic.lastError;
  if (!err) return <span className="text-muted-foreground">{EM_DASH}</span>;
  const head = err.messageHead || t('webhookHealth.shopify.emptyError');
  const tooltip = `${err.errorKind} · ${formatDateTime(err.receivedAt)}`;
  return (
    <span className="block max-w-[24ch] truncate text-destructive" title={`${tooltip}\n${head}`}>
      {head}
    </span>
  );
}

function ProviderBlock({ block }: { block: WebhookProviderHealth }) {
  const { t } = useTranslation();
  const hasDrift = block.topics.some((topic) => !topic.expected);

  return (
    <section className="space-y-3" data-testid={`whh-provider-${block.provider}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">{t(PROVIDER_TITLE_KEY[block.provider])}</h2>
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>
            {t('webhookHealth.shopify.totals.events')}: {block.totals.total}
          </span>
          <span>
            {t('webhookHealth.shopify.totals.errorRate')}: {formatErrorRate(block.totals.errorRate)}
          </span>
        </div>
      </div>

      {hasDrift && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('webhookHealth.shopify.driftAlert')}</span>
        </div>
      )}

      {/* On a card, like every table in the app. This one used to lie bare on
          the page ground with only a border — the grey/white boundary is the
          app's one rule for "this is content", and an exception to it reads as
          a different product rather than as a different page. */}
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('webhookHealth.shopify.table.topic')}</TableHead>
              <TableHead>{t('webhookHealth.shopify.table.status')}</TableHead>
              <TableHead className="text-right">{t('webhookHealth.shopify.table.total')}</TableHead>
              <TableHead className="text-right">
                {t('webhookHealth.shopify.table.completed')}
              </TableHead>
              <TableHead className="text-right">
                {t('webhookHealth.shopify.table.failed')}
              </TableHead>
              <TableHead className="text-right">
                {t('webhookHealth.shopify.table.errorRate')}
              </TableHead>
              <TableHead>{t('webhookHealth.shopify.table.lastSeen')}</TableHead>
              <TableHead>{t('webhookHealth.shopify.table.lastError')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {block.topics.map((topic) => (
              <TableRow key={topic.topic}>
                <TableCell className="font-mono text-xs">{topic.topic}</TableCell>
                <TableCell>
                  <StatusBadge badge={deriveBadge(topic)} />
                </TableCell>
                <TableCell className="text-right tabular-nums">{topic.total}</TableCell>
                <TableCell className="text-right tabular-nums">{topic.completed}</TableCell>
                <TableCell className="text-right tabular-nums">{topic.failed}</TableCell>
                <TableCell
                  className="text-right tabular-nums"
                  data-testid={`whh-errorrate-${topic.topic}`}
                >
                  {formatErrorRate(topic.errorRate)}
                </TableCell>
                <TableCell>
                  <LastSeenCell value={topic.lastSeenAt} />
                </TableCell>
                <TableCell>
                  <LastErrorCell topic={topic} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </section>
  );
}

export default function WebhookHealthPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const windowHours: WebhookHealthWindow = useMemo(() => {
    const raw = Number(searchParams.get('windowHours'));
    return (Number.isFinite(raw) && asWebhookHealthWindow(raw)) || DEFAULT_WEBHOOK_HEALTH_WINDOW;
  }, [searchParams]);

  const { data, isLoading, isError } = useWebhookHealth(windowHours);

  function selectWindow(hours: WebhookHealthWindow) {
    const next = new URLSearchParams(searchParams);
    next.set('windowHours', String(hours));
    setSearchParams(next, { replace: true });
  }

  return (
    // Title and strip go on the shell's pinned bar; everything below stays in
    // the flow. The blurb and the window switcher describe THIS TAB rather than
    // the section, so neither rides the section's bar — the same reason the
    // provider tables do not.
    <PageShell title={t('webhooks.pageTitle')} tabs={<WebhooksTabs />}>
      <p className="text-sm text-muted-foreground">{t('webhookHealth.subtitle')}</p>

      <div
        className="flex items-center gap-2"
        role="group"
        aria-label={t('webhookHealth.shopify.windowSwitcher.label')}
      >
        {WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.hours}
            size="sm"
            variant={opt.hours === windowHours ? 'default' : 'outline'}
            aria-pressed={opt.hours === windowHours}
            onClick={() => selectWindow(opt.hours)}
          >
            {t(opt.labelKey)}
          </Button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">{t('webhookHealth.loading')}</p>}
      {isError && <p className="text-sm text-destructive">{t('webhookHealth.error')}</p>}

      {data && (
        <div className={cn('space-y-8')}>
          {data.providers.map((block) => (
            <ProviderBlock key={block.provider} block={block} />
          ))}
        </div>
      )}
    </PageShell>
  );
}
